import { MentionOptions } from '@tiptap/extension-mention';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from './constants';
import channelService from '../services/channelService';

// Interface for channel members
interface ChannelMember {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
}



// Function to fetch channel members from API
const fetchChannelMembers = async (channelId: string): Promise<ChannelMember[]> => {
  try {
    const response = await channelService.getChannelMembers(channelId, 1, 50);
    
    // Transform the channel service response to match our interface
    const transformedMembers = response.members.map((member: any) => ({
      id: member.id,
      name: member.name,
      email: member.email,
      initials: member.avatarInitials || member.name.charAt(0).toUpperCase(),
      avatarColor: member.avatarColor || getAvatarColor(member.name)
    }));
    
    return transformedMembers;
  } catch (error) {
    console.error('Error fetching channel members:', error);
    // Return empty array if API fails - no fallback to mock users
    return [];
  }
};

// Function to get avatar color based on name
const getAvatarColor = (name: string): string => {
  const colors = ['#1976d2', '#dc004e', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f', '#7b1fa2', '#388e3c'];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Global variable to store channel members (will be set by the component)
let channelMembers: ChannelMember[] = [];
let currentChannelId: string | null = null;

// Function to set channel members (called from ThreadDetailPage)
export const setChannelMembersForMentions = (members: ChannelMember[], channelId: string) => {
  channelMembers = members;
  currentChannelId = channelId;
};

// Function to initialize channel members (called from ThreadDetailPage). Returns members for display/mention mapping.
export const initializeChannelMembers = async (channelId: string): Promise<ChannelMember[]> => {
  try {
    const members = await fetchChannelMembers(channelId);
    channelMembers = members;
    currentChannelId = channelId;
    return members;
  } catch (error) {
    channelMembers = [];
    currentChannelId = channelId;
    return [];
  }
};

export const mentionSuggestionOptions: Partial<MentionOptions> = {
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    char: '@',
    allowSpaces: true,
    startOfLine: false,
    allow: ({ state, range }) => {
      // Always allow mentions for now to debug the issue
      return true;
    },
    items: ({ query }: { query: string }) => {
      
      // If no channel members are available, return empty array
      if (channelMembers.length === 0) {
        return [];
      }
      
      // Always return all users when @ is typed (empty query)
      if (!query || query.trim() === '') {
        return channelMembers;
      }
      
      // Filter users based on query
      const filtered = channelMembers.filter(user => 
        user.name.toLowerCase().includes(query.toLowerCase()) ||
        user.email.toLowerCase().includes(query.toLowerCase())
      );
      return filtered;
    },
    render: () => {
      
      let popup: HTMLElement | null = null;
      let currentCommand: any;
      let selectedIndex: number = 0;

      // Helper function to create popup content
      const createPopupContent = (users: ChannelMember[], query: string) => {
        if (!popup) return;
        
        // Clear existing content
        popup.innerHTML = '';
        
        // If no users available, don't show popup at all
        if (!users || users.length === 0) {
          // Remove the popup if it exists
          if (popup && popup.parentNode) {
            popup.remove();
            popup = null;
          }
          return;
        }
        
        // Create new content
        const contentDiv = document.createElement('div');
        contentDiv.style.padding = '8px';
        
        users.forEach((user: ChannelMember, index: number) => {
          const itemDiv = document.createElement('div');
          itemDiv.className = `mention-item ${index === selectedIndex ? 'selected' : ''}`;
          itemDiv.setAttribute('data-mention-item', index.toString());
          itemDiv.setAttribute('data-user-id', user.id);
          itemDiv.setAttribute('data-user-name', user.name);
          
          // Apply styles
          Object.assign(itemDiv.style, {
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4px',
            backgroundColor: index === selectedIndex ? '#e3f2fd' : 'transparent',
            border: index === selectedIndex ? '1px solid #1976d2' : 'none',
            transition: 'background-color 0.2s ease',
            marginBottom: '2px',
            pointerEvents: 'auto'
          });
          
          // Avatar div
          const avatarDiv = document.createElement('div');
          Object.assign(avatarDiv.style, {
            width: '24px',
            height: '24px',
            backgroundColor: user.avatarColor,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: '500'
          });
          avatarDiv.textContent = user.initials;
          
          // User info div
          const infoDiv = document.createElement('div');
          const nameDiv = document.createElement('div');
          Object.assign(nameDiv.style, {
            fontWeight: '500',
            fontSize: '14px',
            color: '#333'
          });
          nameDiv.textContent = user.name;
          
          const emailDiv = document.createElement('div');
          Object.assign(emailDiv.style, {
            fontSize: '12px',
            color: '#666'
          });
          emailDiv.textContent = user.email;
          
          infoDiv.appendChild(nameDiv);
          infoDiv.appendChild(emailDiv);
          
          // Assemble the item
          itemDiv.appendChild(avatarDiv);
          itemDiv.appendChild(infoDiv);
          
          // Add hover effects
          itemDiv.addEventListener('mouseover', () => {
            // Update selected index
            selectedIndex = index;
            
            // Remove previous selection
            const items = popup?.querySelectorAll('[data-mention-item]');
            items?.forEach((item, i) => {
              (item as HTMLElement).style.backgroundColor = i === selectedIndex ? '#e3f2fd' : 'transparent';
              (item as HTMLElement).style.border = i === selectedIndex ? '1px solid #1976d2' : 'none';
              item.classList.toggle('selected', i === selectedIndex);
            });
          });
          
          // Add click event
          itemDiv.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (currentCommand) {
              currentCommand({ id: user.id, label: user.name });
              // Close the popup after selection
              if (popup && popup.parentNode) {
                popup.remove();
                popup = null;
              }
            } else {
            }
          });
          
          // Add fallback click handler
          itemDiv.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Only execute if the popup is still visible and command is available
            if (popup && popup.parentNode && currentCommand) {
              currentCommand({ id: user.id, label: user.name });
              popup.remove();
              popup = null;
            } else {
            }
          });
          
          contentDiv.appendChild(itemDiv);
        });
        
        popup.appendChild(contentDiv);
      };

      return {
        onStart: (props: SuggestionProps) => {
          // Wrap command to append a space after the mention by including it in the label (reliable across click/Enter)
          currentCommand = (item: { id: string; label: string }) => {
            const labelWithSpace = item.label.trimEnd() + ' ';
            props.command({ id: item.id, label: labelWithSpace });
          };
          selectedIndex = 0;
          
          // Remove any existing popup first
          if (popup && popup.parentNode) {
            popup.remove();
            popup = null;
          }
          
          // Only create popup if there are items available
          if (props.items && props.items.length > 0) {
            // Create popup container
            popup = document.createElement('div');
            popup.className = 'mention-popup';
            popup.style.position = 'absolute';
            popup.style.zIndex = '99999';
            popup.style.backgroundColor = 'white';
            popup.style.border = '1px solid #ddd';
            popup.style.borderRadius = '8px';
            popup.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            popup.style.minWidth = '250px';
            popup.style.maxHeight = '300px';
            popup.style.overflowY = 'auto';
            popup.style.pointerEvents = 'auto';
            document.body.appendChild(popup);

            // Create and populate popup content
            createPopupContent(props.items, props.query || '');
           
            // Position the popup
            const { editor } = props;
            const { view } = editor;
            const { state } = view;
            const { selection } = state;
            const { $from } = selection;
            const coords = view.coordsAtPos($from.pos);

            // Get the editor element to calculate relative positioning
            const editorElement = view.dom.closest('.ProseMirror') || view.dom;
            const editorRect = editorElement.getBoundingClientRect();
            
            // Calculate position relative to the @ character
            let left = coords.left;
            let bottom = 180; // Force bottom to 180px
            
            // Basic horizontal boundary check only
            if (left + 250 > window.innerWidth) {
              left = window.innerWidth - 260;
            }
            
            if (popup) {
              popup.style.left = `${left}px`;
              popup.style.bottom = `${bottom}px`;
            }
          }
        },
        onUpdate: (props: SuggestionProps) => {
          // Wrap command to append a space after the mention by including it in the label (reliable across click/Enter)
          currentCommand = (item: { id: string; label: string }) => {
            const labelWithSpace = item.label.trimEnd() + ' ';
            props.command({ id: item.id, label: labelWithSpace });
          };
          selectedIndex = 0;

          // If no items available, remove popup if it exists
          if (!props.items || props.items.length === 0) {
            if (popup && popup.parentNode) {
              popup.remove();
              popup = null;
            }
            return;
          }

          if (!popup) {
            // Recreate popup if it doesn't exist
            popup = document.createElement('div');
            popup.className = 'mention-popup';
            popup.style.position = 'absolute';
            popup.style.zIndex = '99999';
            popup.style.backgroundColor = 'white';
            popup.style.border = '1px solid #ddd';
            popup.style.borderRadius = '8px';
            popup.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            popup.style.minWidth = '250px';
            popup.style.maxHeight = '300px';
            popup.style.overflowY = 'auto';
            popup.style.pointerEvents = 'auto';
            document.body.appendChild(popup);
          }

          // Update popup content with new filtered results
          createPopupContent(props.items, props.query || '');

           // Update positioning
           const { editor } = props;
           const { view } = editor;
           const { state } = view;
           const { selection } = state;
           const { $from } = selection;
           const coords = view.coordsAtPos($from.pos);

           // Get the editor element to calculate relative positioning
           const editorElement = view.dom.closest('.ProseMirror') || view.dom;
           const editorRect = editorElement.getBoundingClientRect();
           
           // Calculate position relative to the @ character
           let left = coords.left;
           let bottom = 180; // Force bottom to 180px
           
           // Basic horizontal boundary check only
           if (left + 250 > window.innerWidth) {
             left = window.innerWidth - 260;
           }
           
           if (popup) {
             popup.style.left = `${left}px`;
             popup.style.bottom = `${bottom}px`;
           }
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          
          if (props.event.key === 'Escape') {
            if (popup && popup.parentNode) {
              popup.remove();
              popup = null;
            }
            return true;
          }

          // Handle arrow key navigation
          if (props.event.key === 'ArrowDown' || props.event.key === 'ArrowUp') {
            if (!popup) return false;
            const items = popup.querySelectorAll('[data-mention-item]');
            if (items.length === 0) return false;
            
            // Update selected index
            if (props.event.key === 'ArrowDown') {
              selectedIndex = selectedIndex < items.length - 1 ? selectedIndex + 1 : 0;
            } else {
              selectedIndex = selectedIndex > 0 ? selectedIndex - 1 : items.length - 1;
            }
            
            // Update visual selection
            items.forEach((item, index) => {
              (item as HTMLElement).style.backgroundColor = index === selectedIndex ? '#e3f2fd' : 'transparent';
              (item as HTMLElement).style.border = index === selectedIndex ? '1px solid #1976d2' : 'none';
              item.classList.toggle('selected', index === selectedIndex);
            });
            
            // Scroll selected item into view
            const selectedItem = items[selectedIndex] as HTMLElement;
            selectedItem.scrollIntoView({ block: 'nearest' });
            
            return true;
          }

          // Handle Enter key to select
          if (props.event.key === 'Enter') {
            if (!popup) return false;
            const selectedItem = popup.querySelector('[data-mention-item].selected') as HTMLElement;
            if (selectedItem && currentCommand) {
              const userId = selectedItem.getAttribute('data-user-id');
              const userName = selectedItem.getAttribute('data-user-name');
              
              if (userId && userName) {
                currentCommand({ id: userId, label: userName });
                // Close the popup after selection
                if (popup && popup.parentNode) {
                  popup.remove();
                  popup = null;
                }
                return true;
              }
            }
            return false;
          }

          return false;
        },
        onExit: () => {
          if (popup && popup.parentNode) {
            popup.remove();
            popup = null;
          }
        }
      };
    },
  },
};
